import Button from '#app/components/buttons/button'
import {
	TypographyCaps,
	TypographyDisplay,
	TypographyHeading,
	TypographyParagraph,
} from '#app/components/typography'
import { ArrowRightIcon, MoreIcon } from '../icons/index.ts'
import Counter from '#app/components/badgesAndTags/counter'
import { Filter, SearchInput } from '#app/components/inputs'
import Tag from '#app/components/badgesAndTags/tag'
import { useEffect, useRef, useState } from 'react'
import { type artist } from '#types/loreTypes.tsx'
import ArtistsTable from './artistsTable.tsx'

export default function ArtistsList({ artists }: { artists: artist[] }) {
	const [artistsList, setArtistsList] = useState(artists)
	const [currentPage, setCurrentPage] = useState(0)
	const [artistFilter, setArtistFilter] = useState('')
	const [artistSort, setArtistSort] = useState({ field: 'name', order: 'ASC' })
	const [loading, setLoading] = useState(false)
	const prevCurrentPage = useRef(currentPage)
	const prevSortField = useRef(artistSort.field)
	const prevSortOrder = useRef(artistSort.order)
	const prevFilterValue = useRef(artistFilter)

	const loadArtists = async () => {
		setLoading(true)
		prevCurrentPage.current = currentPage
		prevSortField.current = artistSort.field
		prevSortOrder.current = artistSort.order
		prevFilterValue.current = artistFilter
		setTimeout(async () => {
			if (
				prevCurrentPage.current == currentPage &&
				prevSortField.current == artistSort.field &&
				prevSortOrder.current == artistSort.order &&
				prevFilterValue.current == artistFilter
			) {
				try {
					const options = {
						method: 'GET',
						headers: { 'Content-Type': 'application/json' },
					}
					const response = await fetch(
						`/artistsListAPI?page=${currentPage}&sortField=${artistSort.field}&sortOrder=${artistSort.order}&filterField=name&filterValue=${artistFilter}`,
						options,
					)
					const artistIndex = (await response.json()) as { artists: artist[] }
					currentPage == 0
						? setArtistsList(artistIndex.artists)
						: setArtistsList([...artistsList, ...artistIndex.artists])
				} catch (e) {
					console.error(e)
				}
			}
		}, 300)
	}

	useEffect(() => {
		currentPage == 0 ? loadArtists() : setCurrentPage(0)
	}, [artistFilter, artistSort, currentPage])

	useEffect(() => {
		loadArtists()
	}, [currentPage])

	useEffect(() => {
		setLoading(false)
	}, [artistsList])

	return (
		<div className="mx-auto max-w-[1440px] px-6 py-16 sm:px-6 md:px-12 lg:px-36">
			<ArtistListHeader />
			<AllArtists
				artists={artistsList}
				onSortChange={sortOption => setArtistSort(sortOption)}
				onFilterChange={setArtistFilter}
				filterValue={artistFilter}
				loading={loading}
			/>
			{!loading && (
				<div className="flex w-full items-center justify-center">
					<Tag showIcon={false} onClick={() => setCurrentPage(currentPage + 1)}>
						<MoreIcon />
					</Tag>
				</div>
			)}
		</div>
	)
}

const ArtistListHeader = () => {
	return (
		<div className="mb-24 px-0 md:flex md:flex-row md:items-center xl:px-12">
			<div className="flex w-full flex-col md:w-1/2">
				<TypographyCaps
					size="medium"
					additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
				>
					Atomic Lore
				</TypographyCaps>
				<TypographyDisplay
					size="large"
					additionalClasses="mt-8 text-textPrimaryLight dark:text-textPrimaryDark"
				>
					Artists
				</TypographyDisplay>
			</div>
			<div className="mt-6 flex w-full flex-col items-start md:mt-0 md:w-1/2 md:items-end">
				<TypographyParagraph
					size="medium"
					additionalClasses="md:text-right text-left text-textSecondaryLight dark:text-textSecondaryDark"
				>
					Explore the unique collections of digital creators across different
					marketplaces. Dive into their artwork, check out interesting stats,
					and learn more about the artists behind the pieces with verified
					offchain data.
				</TypographyParagraph>
				<Button
					variant="tertiary"
					leftIcon={<ArrowRightIcon />}
					additionalClasses="text-right sm:pr-0 pl-0"
				>
					Create artist profile
				</Button>
			</div>
		</div>
	)
}

const AllArtists = ({
	artists,
	onSortChange,
	onFilterChange,
	filterValue,
	loading,
}: {
	artists: artist[]
	onSortChange: (sortOption: { field: string; order: string }) => void
	onFilterChange: (filterOption: string) => void
	filterValue: string
	loading: boolean
}) => {
	const options = [
		{ title: 'A to Z', description: 'A to Z' },
		{ title: 'Z to A', description: 'Z to A' },
	]

	const filterArtists = (filterOption: string) => {
		onFilterChange(filterOption)
	}

	const sortArtists = (sortOption: { title: string; description: string }) => {
		switch (sortOption.title) {
			case 'A to Z':
				onSortChange({ field: 'name', order: 'ASC' })
				break
			case 'Z to A':
				onSortChange({ field: 'name', order: 'DESC' })
				break
		}
	}

	return (
		<div className="my-24 flex w-full flex-col gap-y-4 px-0 xl:px-12">
			<div className="flex w-full flex-col justify-between sm:flex-row">
				<div className="flex flex-1 items-center gap-x-4">
					<TypographyHeading
						size="medium"
						isBold={true}
						additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
					>
						All Artists
					</TypographyHeading>
					<Counter size="small" children={artists.length} />
				</div>
				<div className="mt-8 flex flex-1 items-center gap-x-4 sm:mt-0 sm:justify-end">
					<SearchInput
						name="Search"
						placeholder="Search"
						showShadow={false}
						value={filterValue}
						setValue={newVal => {
							filterArtists(newVal)
						}}
					/>
					<Filter
						type="Sort"
						options={options}
						onChange={newVal => sortArtists(newVal)}
					></Filter>
				</div>
			</div>
			<div className="pt-6">
				<ArtistsTable data={artists} loading={loading} />
			</div>
		</div>
	)
}
